import React, { useState } from "react";
import DomainDetailsModal from "./DomainDetailsModal";

// Download icon for export button
const DownloadIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// Chevron down icon for dropdown
const ChevronDownIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

// Info icon for tooltip
const InfoIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="12" cy="12" r="10"></circle>
		<path d="M12 16v-4"></path>
		<path d="M12 8h.01"></path>
	</svg>
);

// Get score label based on the ranges
const getScoreLabel = (score) => {
	if (score >= 85) return "Excellent";
	if (score >= 78) return "Great";
	if (score >= 65) return "Acceptable";
	if (score >= 57) return "Poor";
	return "Review Recommended";
};

// SIMPLIFIED: Relevance Score Circle Component - just displays the score
const RelevanceScoreCircle = ({ score }) => {
	// Use the provided score, or fallback only if undefined/null
	const relevanceScore = score !== undefined && score !== null ? score : 75;

	// Determine color based on score
	const getScoreColor = (score) => {
		if (score >= 85) return "#155724";
		if (score >= 78) return "#0c5460";
		if (score >= 65) return "#856404";
		if (score >= 57) return "#721c24";
		return "#6c757d"; // Red
	};

	return (
		<div
			style={{
				width: "48px",
				height: "48px",
				borderRadius: "50%",
				backgroundColor: getScoreColor(relevanceScore),
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				color: "white",
				fontWeight: "600",
				fontSize: "12px",
				border: "2px solid white",
				boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
			}}
		>
			{Math.round(relevanceScore)}
		</div>
	);
};

// Enhanced Tooltip Component
const Tooltip = ({ children, text, position = "top" }) => {
	const [isVisible, setIsVisible] = useState(false);

	return (
		<div
			style={{
				position: "relative",
				display: "inline-flex",
				alignItems: "center",
			}}
			onMouseEnter={() => setIsVisible(true)}
			onMouseLeave={() => setIsVisible(false)}
		>
			{children}
			{isVisible && (
				<div
					style={{
						position: "absolute",
						bottom: position === "top" ? "100%" : "auto",
						top: position === "bottom" ? "100%" : "auto",
						left: "50%",
						transform: "translateX(-50%)",
						backgroundColor: "#1f2937",
						color: "white",
						padding: "8px 12px",
						borderRadius: "6px",
						fontSize: "11px",
						whiteSpace: "nowrap",
						zIndex: 1000,
						marginBottom: position === "top" ? "8px" : "0",
						marginTop: position === "bottom" ? "8px" : "0",
						boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
						maxWidth: "200px",
						whiteSpace: "normal",
						textAlign: "center",
						lineHeight: "1.3",
					}}
				>
					{text}
					{/* Arrow */}
					<div
						style={{
							position: "absolute",
							left: "50%",
							transform: "translateX(-50%)",
							width: 0,
							height: 0,
							borderLeft: "6px solid transparent",
							borderRight: "6px solid transparent",
							borderTop: position === "top" ? "none" : "6px solid #1f2937",
							borderBottom: position === "top" ? "6px solid #1f2937" : "none",
							top: position === "top" ? "100%" : "auto",
							bottom: position === "bottom" ? "100%" : "auto",
						}}
					/>
				</div>
			)}
		</div>
	);
};

// Export Dropdown Component for Set Cards
const SetExportDropdown = ({ setData }) => {
	const [isOpen, setIsOpen] = useState(false);

	// Helper function to properly escape CSV values
	const escapeCsvValue = (value) => {
		if (value === null || value === undefined) {
			return "";
		}

		// Convert to string
		let stringValue = String(value);

		// If the value contains comma, newline, or quote, wrap in quotes and escape internal quotes
		if (
			stringValue.includes(",") ||
			stringValue.includes("\n") ||
			stringValue.includes("\r") ||
			stringValue.includes('"')
		) {
			// Escape internal quotes by doubling them
			stringValue = stringValue.replace(/"/g, '""');
			// Wrap in quotes
			return `"${stringValue}"`;
		}

		return stringValue;
	};

	// Helper function to format arrays for CSV
	const formatArrayForCsv = (arr) => {
		if (!Array.isArray(arr)) {
			return escapeCsvValue(arr);
		}
		// Join array elements with semicolon to avoid comma conflicts
		const joinedValue = arr
			.filter((item) => item !== null && item !== undefined && item !== "")
			.join("; ");
		return escapeCsvValue(joinedValue);
	};

	const exportSummarized = () => {
		const headers = ["Domain", "Market Requests", "Score", "Categories"];
		const csvContent = [
			headers.join(","),
			...setData.domains.map((domain) => {
				const domainName =
					domain.name ||
					domain.domain ||
					domain.domainName ||
					domain.url ||
					domain.hostname ||
					domain;
				const marketRequest =
					domain.totalCprMarketRequests || domain.metrics?.marketRequests || 0;
				const score = domain.score || domain.metrics?.score || 0;
				const categories =
					domain.categories || domain.metrics?.categories || [];

				return [
					escapeCsvValue(
						typeof domainName === "string" ? domainName : "Unknown Domain",
					),
					escapeCsvValue(marketRequest),
					escapeCsvValue(score),
					formatArrayForCsv(categories),
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_summarized.csv`,
		);
		setIsOpen(false);
	};

	const exportDetailed = () => {
		const headers = [
			"Domain Name",
			"Domain Reach",
			"Score",
			"Composite Score",
			"Cost Performance Score",
			"Performance Score",
			"Categories",
			"Description",
			"Intended Audience",
			"Content Styles",
			"Geography",
			"IAB Media Subtype",
			"Video Placement",
			"IAB Device Type",
			"Publisher Account IDs",
			"Is Direct",
			"Safety Tier",
			"Total CPR Clicks",
			"Total Impressions with Click Macro",
			"Total CPR Market Demand Partner Spend (USD)",
			"Total CPR Market Publisher Revenue (USD)",
			"Total CPR Market Requests",
			"Total CPR All Requests",
			"Total CPR Private Market Impressions",
			"Total Complete First Occurrence",
			"Total Start First Occurrence",
			"Total Viewables",
			"Total Viewability Measures",
			"Total Fills",
			"Total Non Zero Bids",
			"Total Impressions",
			"Total Deal Volume Deal Info",
			"Total Non Zero Bids Value (USD)",
			"Total Bid Spend (USD)",
			"Calculated Revenue Per Impression",
			"Calculated Cost Per Impression",
			"Calculated Bid Guidance",
			"Calculated CTR",
			"Average VTR",
			"Average Viewability",
			"Average Ox Win Rate",
			"Average Pub Win Rate",
			"Average Bid CPM",
			"Publisher",
			"Reach",
			"Relevance",
			"Completion Rate",
		];

		// Use rawDomains if available, otherwise fall back to domains
		const domainsToExport = setData.rawDomains || setData.domains;
		const setRelevanceScore = setData.relevanceScore || "N/A";

		const csvContent = [
			headers.join(","),
			...domainsToExport.map((domain) => {
				return [
					escapeCsvValue(
						domain.domainName ||
							domain.domain ||
							domain.name ||
							"Unknown Domain",
					),
					escapeCsvValue(domain.domainReach || 0),
					escapeCsvValue(domain.score || 0),
					escapeCsvValue(domain.compositeScore || 0),
					escapeCsvValue(domain.costPerfomanceScore || 0),
					escapeCsvValue(domain.perfomanceScore || 0),
					formatArrayForCsv(domain.categories || []),
					escapeCsvValue(domain.description || "N/A"),
					escapeCsvValue(domain.intendedAudience || "N/A"),
					formatArrayForCsv(domain.content_styles || []),
					formatArrayForCsv(domain.geography || []),
					formatArrayForCsv(domain.iabmediasubtype || []),
					formatArrayForCsv(domain.videoplcmt || []),
					formatArrayForCsv(domain.iabdevicetype || []),
					formatArrayForCsv(domain.publisheraccountid || []),
					formatArrayForCsv(domain.isDirect || []),
					formatArrayForCsv(domain.safetyTier || []),
					escapeCsvValue(domain.totalCprClicks || 0),
					escapeCsvValue(domain.totalImpressionsWithClickMacro || 0),
					escapeCsvValue(domain.totalCprMarketDemandPartnerSpendInUsd || 0),
					escapeCsvValue(domain.totalCprMarketPublisherRevenueInUsd || 0),
					escapeCsvValue(domain.totalCprMarketRequests || 0),
					escapeCsvValue(domain.totalCprAllRequests || 0),
					escapeCsvValue(domain.totalCprPrivateMarketImpressions || 0),
					escapeCsvValue(domain.totalCompleteFirstOccurrence || 0),
					escapeCsvValue(domain.totalStartFirstOccurrence || 0),
					escapeCsvValue(domain.totalViewables || 0),
					escapeCsvValue(domain.totalViewabilityMeasures || 0),
					escapeCsvValue(domain.totalFills || 0),
					escapeCsvValue(domain.totalNonZeroBids || 0),
					escapeCsvValue(domain.totalImpressions || 0),
					escapeCsvValue(domain.totalDealVolumeDealInfo || 0),
					escapeCsvValue(domain.totalNonZeroBidsValueInUsd || 0),
					escapeCsvValue(domain.totalBidSpendInUsd || 0),
					escapeCsvValue(domain.calculated_revenueperimpression || 0),
					escapeCsvValue(domain.calculated_costperimpression || 0),
					escapeCsvValue(domain.calculated_bid_guidance || 0),
					escapeCsvValue(domain.calculated_ctr || 0),
					escapeCsvValue(domain.avgVtr || 0),
					escapeCsvValue(domain.avgViewability || 0),
					escapeCsvValue(domain.avgOxWinRate || 0),
					escapeCsvValue(domain.avgPubWinRate || 0),
					escapeCsvValue(domain.avgBidCpm || 0),
					escapeCsvValue(domain.publisher || "N/A"),
					escapeCsvValue(domain.reach || 0),
					escapeCsvValue(setRelevanceScore),
					escapeCsvValue(domain.completionRate || 0),
				].join(",");
			}),
		].join("\n");

		downloadCSV(
			csvContent,
			`${setData.name.replace(/\s+/g, "_")}_detailed.csv`,
		);
		setIsOpen(false);
	};

	const downloadCSV = (content, filename) => {
		// Add BOM for proper Excel UTF-8 handling
		const BOM = "\uFEFF";
		const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8;" });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a); // Required for Firefox
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	};

	const handleDropdownToggle = (e) => {
		e.stopPropagation(); // Prevent card click
		setIsOpen(!isOpen);
	};

	return (
		<div style={{ position: "relative", zIndex: 10 }}>
			<button
				onClick={handleDropdownToggle}
				className="btn-medium-secondary"
				style={{
					display: "flex",
					alignItems: "center",
					gap: "4px",
					fontSize: "10px",
					padding: "4px 8px",
				}}
			>
				<DownloadIcon />
				Export
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<>
					{/* Overlay to close dropdown when clicking outside */}
					<div
						style={{
							position: "fixed",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 5,
						}}
						onClick={() => setIsOpen(false)}
					/>
					<div
						style={{
							position: "absolute",
							top: "100%",
							right: 0,
							backgroundColor: "white",
							border: "1px solid var(--openx-border)",
							borderRadius: "4px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
							zIndex: 10,
							minWidth: "140px",
							marginTop: "2px",
						}}
					>
						<button
							onClick={exportSummarized}
							style={{
								width: "100%",
								padding: "10px 12px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "11px",
								fontWeight: "bold",
								color: "var(--openx-text-primary)",
								transition: "background-color 0.2s ease",
								display: "flex",
								alignItems: "center",
								gap: "8px",
							}}
							onMouseEnter={(e) =>
								(e.target.style.backgroundColor = "var(--openx-surface)")
							}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Summarized
						</button>
						{/* Separator line */}
						<div
							style={{
								height: "1px",
								backgroundColor: "var(--openx-border)",
								margin: "0 8px",
							}}
						/>
						<button
							onClick={exportDetailed}
							style={{
								width: "100%",
								padding: "10px 12px",
								border: "none",
								background: "none",
								textAlign: "left",
								cursor: "pointer",
								fontSize: "11px",
								fontWeight: "bold",
								color: "var(--openx-text-primary)",
								transition: "background-color 0.2s ease",
								display: "flex",
								alignItems: "center",
								gap: "8px",
							}}
							onMouseEnter={(e) =>
								(e.target.style.backgroundColor = "var(--openx-surface)")
							}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							Export Detailed
						</button>
					</div>
				</>
			)}
		</div>
	);
};

export const PrebuiltSets = ({ prebuiltDomainSets }) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [modalSelectedSet, setModalSelectedSet] = useState(null);

	const openDomainDetails = (setKey) => {
		setModalSelectedSet(setKey);
		setModalOpen(true);
	};

	const closeDomainDetails = () => {
		setModalOpen(false);
		setModalSelectedSet(null);
	};

	return (
		<>
			<style>
				{`
          .prebuild-sets-container * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
          }
          .prebuild-sets-container .card-container {
            border: 1px solid var(--openx-border) !important;
            box-shadow: none !important;
          }
          .prebuild-sets-container .card-container:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
          }
        `}
			</style>
			<div
				className="prebuild-sets-container"
				style={{
					backgroundColor: "var(--openx-surface)",
					border: "1px solid var(--openx-border)",
					borderRadius: "8px",
					padding: "20px",
					marginBottom: "12px",
				}}
			>
				<h3
					style={{
						fontSize: "16px",
						fontWeight: "500",
						color: "var(--openx-text-primary)",
						marginBottom: "16px",
						border: "none",
						outline: "none",
						background: "none",
						boxShadow: "none",
					}}
				>
					Prebuild Domain Sets
				</h3>

				<div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
					{Object.entries(prebuiltDomainSets).map(([key, set]) => {
						// SIMPLIFIED: Just use the pre-calculated relevance score from the calculator
						const relevanceScore =
							set.relevanceScore || set.performanceScore || 75;

						return (
							<div
								key={key}
								className="card-container"
								style={{
									borderRadius: "8px",
									padding: "16px",
									backgroundColor: "#ffffff",
									cursor: "pointer",
									transition: "all 0.2s ease",
									minWidth: "240px",
									flex: "1",
									position: "relative",
								}}
								onClick={() => openDomainDetails(key)}
							>
								{/* Header with title and relevance score circle */}
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "flex-start",
										marginBottom: "8px",
									}}
								>
									<div style={{ flex: 1 }}>
										<div
											style={{
												fontWeight: "600",
												color: "var(--openx-text-primary)",
												marginBottom: "4px",
												fontSize: "14px",
												border: "none",
												outline: "none",
												background: "none",
												boxShadow: "none",
											}}
										>
											{set.name}
										</div>
										<div
											style={{
												fontSize: "12px",
												color: "var(--openx-text-secondary)",
												marginBottom: "12px",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											{set.description}
										</div>
									</div>
									<Tooltip
										text={`Average relevance score: ${relevanceScore} (${getScoreLabel(relevanceScore)})`}
									>
										<RelevanceScoreCircle score={relevanceScore} />
									</Tooltip>
								</div>

								{/* First row - Domains, Market Reach, and Avg. Reach Per Day */}
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										marginBottom: "12px",
										gap: "8px",
									}}
								>
									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											Domains
										</div>
										<div
											style={{
												fontSize: "16px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											{set.domains?.length || 0}
										</div>
									</div>

									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											Market Requests
										</div>
										<div
											style={{
												fontSize: "16px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											{set.marketRequests || "0"}
										</div>
									</div>

									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
												border: "none",
												outline: "none",
												background: "none",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: "4px",
											}}
										>
											<Tooltip text="This is a domain level reach without any filter restrictions">
												<span
													style={{
														display: "flex",
														alignItems: "center",
														gap: "4px",
													}}
												>
													Avg. Reach/Day
													<InfoIcon />
												</span>
											</Tooltip>
										</div>
										<div
											style={{
												fontSize: "16px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
												border: "none",
												outline: "none",
												background: "none",
											}}
										>
											{set.totalReach || "0"}
										</div>
									</div>
								</div>

								{/* Second row - CTR/VTR, Viewability, Bid Guidance */}
								<div
									style={{
										display: "flex",
										justifyContent: "space-between",
										marginBottom: "12px",
										gap: "8px",
									}}
								>
									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
											}}
										>
											CTR/VTR
										</div>
										<div
											style={{
												fontSize: "12px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
											}}
										>
											{set.metrics?.ctr?.value || "0.0%"}/
											{set.metrics?.vtr?.value || "0.0%"}
										</div>
										<div
											style={{
												fontSize: "10px",
												color:
													set.metrics?.ctr?.trend === "up"
														? "#10b981"
														: set.metrics?.ctr?.trend === "down"
															? "#ef4444"
															: "var(--openx-text-secondary)",
											}}
										>
											{set.metrics?.ctr?.change || ""}
										</div>
									</div>

									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
											}}
										>
											Viewability
										</div>
										<div
											style={{
												fontSize: "12px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
											}}
										>
											{set.metrics?.viewability?.value || "0.0%"}
										</div>
										<div
											style={{
												fontSize: "10px",
												color:
													set.metrics?.viewability?.trend === "up"
														? "#10b981"
														: set.metrics?.viewability?.trend === "down"
															? "#ef4444"
															: "var(--openx-text-secondary)",
											}}
										>
											{set.metrics?.viewability?.change || ""}
										</div>
									</div>

									<div style={{ textAlign: "center", flex: 1 }}>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "2px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
											}}
										>
											Bid Guidance
										</div>
										<div
											style={{
												fontSize: "12px",
												fontWeight: "600",
												color: "var(--openx-text-primary)",
											}}
										>
											{set.metrics?.cpm?.value || "$0.00"}
										</div>
									</div>
								</div>

								{/* Content Categories */}
								{set.categories && set.categories.length > 0 && (
									<div
										style={{
											marginBottom: "8px",
										}}
									>
										<div
											style={{
												fontSize: "10px",
												color: "var(--openx-text-secondary)",
												marginBottom: "4px",
												textTransform: "uppercase",
												letterSpacing: "0.5px",
											}}
										>
											Categories
										</div>
										<div
											style={{
												display: "flex",
												flexWrap: "wrap",
												gap: "4px",
											}}
										>
											{set.categories.map((category, idx) => (
												<span
													key={idx}
													style={{
														fontSize: "10px",
														padding: "3px 8px",
														backgroundColor: "#f5f5f5",
														color: "#666666",
														borderRadius: "10px",
														border: "1px solid #e5e7eb",
														fontWeight: "500",
													}}
												>
													{category}
												</span>
											))}
										</div>
									</div>
								)}

								{/* Footer with View Details (left) and Export (right) */}
								<div
									style={{
										fontSize: "11px",
										fontWeight: "500",
										color: "var(--openx-text-primary)",
										borderTop: "1px solid var(--openx-border)",
										paddingTop: "8px",
										marginTop: "8px",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<span style={{ color: "#666666" }}>View Details →</span>
									<div onClick={(e) => e.stopPropagation()}>
										<SetExportDropdown setData={set} />
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Domain Details Modal */}
			<DomainDetailsModal
				isOpen={modalOpen}
				onClose={closeDomainDetails}
				selectedSet={modalSelectedSet}
				prebuiltDomainSets={prebuiltDomainSets}
			/>
		</>
	);
};
